import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useField } from "formik";
import { object, string } from "yup";
import {
  Button,
  Card,
  Editor,
  ErrorMessage,
  Flex,
  Label,
  OverlayStyle,
  SlideoutDrawerV2,
  Text,
  useCopy,
} from "@jsq/ds/core";
import {
  Form,
  FormRadioButton,
  FormRadioGroup,
  FormSelect,
  FormSwitch,
  FormTextInput,
  SubmitButton,
} from "@jsq/ds/forms";
import { Colors } from "@jsq/ds/styles";
import { SPACING } from "~/styles/variables";
import { RulesSection } from "../../../../shared/RulesSection";
import { rulesSchema } from "../../../../shared/RulesSection/validation";
import { useMergeTagsEditorInit } from "../../useMergeTagsEditorInit";
import { MERGE_FIELD_PLACEHOLDER, toSymbolTemplateTag } from "../footnotesMapper";
import {
  FOOTNOTE_SYMBOL_OPTIONS,
  FOOTNOTE_SYMBOL_SHORT_LABELS,
} from "../QuerySettingsPanel/constants";
import { NAME_MAX_LENGTH } from "./constants";
import {
  buildFootnoteMergeTags,
  findUnknownMergeFields,
  mergeFieldValue,
  type FootnoteMergeTag,
  type FootnoteMergeTagSource,
} from "./footnoteMergeTags";
import { toFootnoteSettingsValues } from "./footnoteSettingsMapper";
import * as styles from "./styles_vanilla.css";
import { Z_INDEX } from "./zIndex";
import type { CustomFieldOptionGroup, FootnoteSettingsValues } from "./types";
import type { AttributeLeaf } from "../../../../shared/RulesSection/attributeCatalogue";
import type { FootnoteSymbolOption } from "../QuerySettingsPanel/types";
import type { QueryFootnoteFieldsFragment } from "~/graphql/supergraphTypes";

type FootnoteSettingsPanelProps = {
  isOpen: boolean;
  onClose: () => void;
  /** Footnote being edited, or a blank fragment for the create flow. */
  footnoteRaw: QueryFootnoteFieldsFragment | null;
  onSave: (values: FootnoteSettingsValues) => void;
  isReadOnly?: boolean;
  /** Arena scope of the query this footnote belongs to. `null` for system-level. */
  arenaId: number | null;
  attributeLeaves?: ReadonlyArray<AttributeLeaf>;
  /** Query fields a freeform footnote's text can reference as merge fields. */
  fieldOptions?: ReadonlyArray<FootnoteMergeTagSource>;
  /** Arena GPX custom fields a `CUSTOM_FIELD` footnote can pull text from,
   * sectioned by the object type they're defined on. */
  customFieldOptionGroups?: ReadonlyArray<CustomFieldOptionGroup>;
  /** True while `customFieldOptionGroups` are still loading. */
  isLoadingCustomFields?: boolean;
  /** True when loading `customFieldOptionGroups` failed. */
  hasCustomFieldsError?: boolean;
  /** Query-level default symbol style, named in the override toggle label. */
  queryFootnoteSymbol: FootnoteSymbolOption;
  /** Trimmed names held by the query's *other* footnotes — the caller drops the
   * one being edited so a rename that keeps the name doesn't clash with itself. */
  takenNames?: ReadonlySet<string>;
  /** `CustomFieldOption.id`s already sourced by the query's other custom-field
   * footnotes. */
  takenCustomFieldIds?: ReadonlySet<string>;
};

/** Shared default so an omitted prop keeps a stable identity across renders and
 * doesn't rebuild the validation schema. */
const EMPTY_SET: ReadonlySet<string> = new Set();

const NAME_NOT_UNIQUE_ERROR = "Name already taken. Enter a unique name.";
const CUSTOM_FIELD_IN_USE_ERROR = "This custom field already has a footnote in this query.";
const DERIVED_NAME_NOT_UNIQUE_ERROR =
  "Another footnote already uses this custom field's name. Rename or remove that footnote first.";

/** Unlike query names, the backend compares footnote names case-sensitively
 * (`_enforce_footnote_name_uniqueness` in `finpub-data-service`; differing
 * cases get distinct merge-field slug variants).*/
const makeValidationSchema = (
  knownMergeFields: ReadonlySet<string>,
  takenNames: ReadonlySet<string>,
  takenCustomFieldIds: ReadonlySet<string>,
  customFieldNamesById: ReadonlyMap<string, string>,
) =>
  object({
    // Custom-field footnotes derive their (non-editable) name from the selected
    // custom field, so the name is only validated — required *and* length-capped
    // — for freeform footnotes
    name: string()
      .trim()
      .when("footnoteKind", {
        is: "FREEFORM_TEXT",
        then: (schema) =>
          schema
            .required("Name the footnote")
            .max(NAME_MAX_LENGTH, `Maximum ${NAME_MAX_LENGTH} characters`)
            .test("unique-name", NAME_NOT_UNIQUE_ERROR, (value) =>
              value ? !takenNames.has(value.trim()) : true,
            ),
        otherwise: (schema) => schema.notRequired(),
      }),
    text: string().when("footnoteKind", {
      is: "FREEFORM_TEXT",
      then: (schema) =>
        schema
          .trim()
          .required("Input footnote text")
          .test(
            "known-merge-fields",
            "Footnote references a data field that is not in this query",
            function (value) {
              const unknown = findUnknownMergeFields(value ?? "", knownMergeFields);
              if (unknown.length === 0) return true;
              return this.createError({
                message: `Remove data fields that are no longer in this query: ${unknown.join(", ")}`,
              });
            },
          ),
      otherwise: (schema) => schema.notRequired(),
    }),
    // The name input isn't rendered for this kind, so both ways a custom-field
    // footnote can collide have to surface on the picker: picking a field that
    // already has a footnote, and picking one whose label is taken by another
    // footnote (a freeform note, or a same-named field on another object type).
    customFieldId: string().when("footnoteKind", {
      is: "CUSTOM_FIELD",
      then: (schema) =>
        schema
          .required("Select a custom field")
          .test("unique-custom-field", CUSTOM_FIELD_IN_USE_ERROR, (value) =>
            value ? !takenCustomFieldIds.has(value) : true,
          )
          // Read the label off the picker options rather than the sibling
          // `name`, which the picker only syncs in an effect — a render behind
          // the validation this selection kicks off.
          .test("unique-derived-name", DERIVED_NAME_NOT_UNIQUE_ERROR, (value) => {
            if (!value) return true;
            const derivedName = customFieldNamesById.get(value)?.trim();
            return derivedName ? !takenNames.has(derivedName) : true;
          }),
      otherwise: (schema) => schema.notRequired(),
    }),
    suppressionRules: rulesSchema,
  });

/** Footnote name. Shown only for freeform footnotes */
const NameField = ({ isReadOnly }: { isReadOnly: boolean }) => {
  const [{ value: footnoteKind }] = useField<string>("footnoteKind");
  if (footnoteKind !== "FREEFORM_TEXT") return null;
  return (
    <FormTextInput
      name="name"
      label="Name"
      className={styles.formControl}
      disabled={isReadOnly}
      data-testid="footnote-settings-name"
    />
  );
};

/** Freeform body.
 *
 * A TinyMCE mergetags editor: data-field tokens render as canonical
 * `{d.<merge_field>}` chips, inserted through the `{` autocompleter, which
 * matches on the field's friendly name.
 *
 * The editor is uncontrolled and seeds from Formik once per mount, so the guard
 * lives here rather than as an early return inside `FootnoteTextEditor`: React
 * keeps a component mounted through an early return, which would hold the seed
 * at whatever the text was when the panel opened. */
const FootnoteTextField = ({
  isReadOnly,
  mergeTags,
}: {
  isReadOnly: boolean;
  mergeTags: ReadonlyArray<FootnoteMergeTag>;
}) => {
  const [{ value: footnoteKind }] = useField<string>("footnoteKind");
  if (footnoteKind !== "FREEFORM_TEXT") return null;
  return <FootnoteTextEditor isReadOnly={isReadOnly} mergeTags={mergeTags} />;
};

const FootnoteTextEditor = ({
  isReadOnly,
  mergeTags,
}: {
  isReadOnly: boolean;
  mergeTags: ReadonlyArray<FootnoteMergeTag>;
}) => {
  const [{ value: text }, meta, helpers] = useField<string>("text");
  // Seeded once per mount, from whatever Formik holds now — which covers both
  // remount triggers: switching the footnote type away and back, and selecting
  // a different footnote (the Form is keyed on it).
  const [initialText] = useState(text);

  const helpersRef = useRef(helpers);
  helpersRef.current = helpers;

  const handleEditorChange = useCallback((plainText: string) => {
    void helpersRef.current.setValue(plainText);
  }, []);

  const editorTags = useMemo(
    () =>
      mergeTags.map((tag) => ({
        value: mergeFieldValue(tag.mergeField),
        title: tag.label,
      })),
    [mergeTags],
  );

  const editorInit = useMergeTagsEditorInit({
    autocompleterId: "footnote-mergetags-after-bracket",
    placeholder: "Enter footnote text",
    height: 160,
    tags: editorTags,
    mergetagsSuffix: "}",
    formatToken: (value) => `{${value}}`,
  });

  return (
    <Flex flexDirection="column" gap={SPACING.X_SMALL}>
      <Label>Text</Label>
      <Editor
        init={editorInit}
        initialValue={initialText}
        disabled={isReadOnly}
        onEditorChange={(_content, editor) => {
          handleEditorChange(editor.getContent({ format: "text" }));
        }}
        data-testid="footnote-settings-text"
      />
      {meta.touched && meta.error && (
        <ErrorMessage data-testid="footnote-settings-text-error">{meta.error}</ErrorMessage>
      )}
      {!isReadOnly && mergeTags.length > 0 && (
        <Text variant="secondary-sm">Type &#123; to insert data fields</Text>
      )}
    </Flex>
  );
};

/** Custom-field source picker. Shown only for custom-field footnotes; the
 * selection sets the footnote's `customFieldId`. Options are sectioned by the
 * object type the custom field is defined on. */
const CustomFieldPicker = ({
  customFieldOptionGroups,
  isLoadingCustomFields,
  hasCustomFieldsError,
  isReadOnly,
}: {
  customFieldOptionGroups: ReadonlyArray<CustomFieldOptionGroup>;
  isLoadingCustomFields: boolean;
  hasCustomFieldsError: boolean;
  isReadOnly: boolean;
}) => {
  const [{ value: footnoteKind }] = useField<string>("footnoteKind");
  const [{ value: customFieldId }] = useField<string>("customFieldId");
  const [, , nameHelpers] = useField<string>("name");
  const { setValue: setName } = nameHelpers;

  // A custom-field footnote's name mirrors the selected custom field's name
  const selectedName = useMemo(
    () =>
      customFieldOptionGroups
        .flatMap((group) => group.options)
        .find((option) => option.id === customFieldId)?.name,
    [customFieldOptionGroups, customFieldId],
  );
  const prevFootnoteKind = useRef(footnoteKind);
  useEffect(() => {
    const cameFromCustomField = prevFootnoteKind.current === "CUSTOM_FIELD";
    prevFootnoteKind.current = footnoteKind;
    if (footnoteKind === "CUSTOM_FIELD") {
      if (selectedName !== undefined) void setName(selectedName);
    } else if (cameFromCustomField) {
      // Switching away from a custom-field footnote: drop the auto-derived name
      // so a freeform footnote doesn't inherit the custom field's label.
      void setName("");
    }
  }, [footnoteKind, selectedName, setName]);

  if (footnoteKind !== "CUSTOM_FIELD") return null;
  const placeholder = hasCustomFieldsError
    ? "Couldn\u2019t load custom fields"
    : isLoadingCustomFields
      ? "Loading custom fields\u2026"
      : "Search custom fields";
  return (
    <Flex flexDirection="column" alignItems="flex-start" gap={SPACING.X_SMALL}>
      <Text variant="strong-md">Custom field</Text>
      <FormSelect
        name="customFieldId"
        label="Custom field"
        hideLabel
        placeholder={placeholder}
        className={styles.formControl}
        disabled={isReadOnly || isLoadingCustomFields || hasCustomFieldsError}
        data-testid="footnote-settings-custom-field"
      >
        {customFieldOptionGroups.map((group) => (
          <FormSelect.Section key={group.sectionLabel} name={group.sectionLabel}>
            {group.options.map((option) => (
              <FormSelect.Option key={option.id} value={option.id}>
                {option.name}
              </FormSelect.Option>
            ))}
          </FormSelect.Section>
        ))}
      </FormSelect>
      {hasCustomFieldsError && (
        <Text variant="secondary-sm" className={styles.helperText}>
          Something went wrong loading custom fields. Try reopening this panel.
        </Text>
      )}
    </Flex>
  );
};

/** Read-only, copyable Carbone tag an author pastes into a template to place
 * the footnote's symbol (see `toSymbolTemplateTag` for the grammar).
 */
const SymbolMergeField = ({ persistedToken }: { persistedToken: string }) => {
  const { copy, attemptedCopy } = useCopy({ useTimeout: true });

  // The server keeps a footnote's slug for its lifetime, so the token
  // outlives any rename and only an unsaved footnote is without one.
  const hasToken = persistedToken.length > 0;
  const token = hasToken ? toSymbolTemplateTag(persistedToken) : MERGE_FIELD_PLACEHOLDER;

  return (
    <Flex flexDirection="column" alignItems="flex-start" gap={SPACING.X_SMALL}>
      <Text variant="strong-md">Symbol merge field</Text>
      <Text variant="secondary-sm" className={styles.helperText}>
        Copy and paste this merge field footnote symbol beside the data field merge field in the
        template
      </Text>
      <Flex className={styles.mergeFieldBox} data-testid="footnote-settings-symbol-merge-field">
        <Text variant="regular-sm" className={styles.mergeFieldBoxText}>
          {token}
        </Text>
        {hasToken && (
          <Button
            icon={attemptedCopy ? "check" : "copy"}
            variant="muted"
            onClick={() => void copy(token)}
            aria-label="Copy symbol merge field"
          />
        )}
      </Flex>
    </Flex>
  );
};

const FootnoteSymbolField = ({
  queryFootnoteSymbol,
  isReadOnly,
}: {
  queryFootnoteSymbol: FootnoteSymbolOption;
  isReadOnly: boolean;
}) => {
  const [{ value: overrideFootnoteSymbol }] = useField<boolean>("overrideFootnoteSymbol");
  const defaultLabel = FOOTNOTE_SYMBOL_SHORT_LABELS[queryFootnoteSymbol];

  return (
    <Flex flexDirection="column" gap={SPACING.X_SMALL} className={styles.symbolControl}>
      <Text variant="strong-md">Symbol display</Text>
      <FormSwitch
        name="overrideFootnoteSymbol"
        id="footnote-settings-override-symbol"
        label={`Override query default (${defaultLabel})`}
        size="small"
        disabled={isReadOnly}
        data-testid="footnote-settings-override-symbol"
      />
      {overrideFootnoteSymbol && (
        <FormSelect
          name="footnoteSymbol"
          label="Footnote symbol"
          hideLabel
          className={styles.formControl}
          disabled={isReadOnly}
          data-testid="footnote-settings-symbol-style"
        >
          <FormSelect.Section>
            {FOOTNOTE_SYMBOL_OPTIONS.map((option) => (
              <FormSelect.Option key={option.value} value={option.value}>
                {option.label}
              </FormSelect.Option>
            ))}
          </FormSelect.Section>
        </FormSelect>
      )}
    </Flex>
  );
};

export const FootnoteSettingsPanel = ({
  isOpen,
  onClose,
  footnoteRaw,
  onSave,
  isReadOnly = false,
  arenaId,
  attributeLeaves,
  fieldOptions = [],
  customFieldOptionGroups = [],
  isLoadingCustomFields = false,
  hasCustomFieldsError = false,
  queryFootnoteSymbol,
  takenNames = EMPTY_SET,
  takenCustomFieldIds = EMPTY_SET,
}: FootnoteSettingsPanelProps) => {
  // Cache the most-recent non-null footnote so the drawer keeps rendering its
  // contents while it animates closed (the parent clears `footnoteRaw` on
  // save/close). `onCloseAnimationComplete` clears the cache so a reopen
  // re-mounts the Form with fresh `initialValues`. Mirrors FieldSettingsPanel.
  const [displayRaw, setDisplayRaw] = useState<QueryFootnoteFieldsFragment | null>(footnoteRaw);
  if (footnoteRaw && footnoteRaw !== displayRaw) {
    setDisplayRaw(footnoteRaw);
  }

  const handleCloseAnimationComplete = useCallback(() => {
    setDisplayRaw(null);
  }, []);

  const initialValues = useMemo(
    () => (displayRaw ? toFootnoteSettingsValues(displayRaw, queryFootnoteSymbol) : null),
    [displayRaw, queryFootnoteSymbol],
  );

  const isSystemLevel = arenaId == null;

  const mergeTags = useMemo(() => buildFootnoteMergeTags(fieldOptions), [fieldOptions]);
  // Validation keys off the authoritative field set (every field's merge tag),
  // not the display labels, so a label collision can't flag a real field.
  const knownMergeFields = useMemo(
    () => new Set(fieldOptions.map((option) => option.mergeField)),
    [fieldOptions],
  );
  const customFieldNamesById = useMemo(
    () =>
      new Map(
        customFieldOptionGroups.flatMap((group) =>
          group.options.map((option) => [option.id, option.name] as const),
        ),
      ),
    [customFieldOptionGroups],
  );
  const validationSchema = useMemo(
    () =>
      makeValidationSchema(
        knownMergeFields,
        takenNames,
        takenCustomFieldIds,
        customFieldNamesById,
      ),
    [knownMergeFields, takenNames, takenCustomFieldIds, customFieldNamesById],
  );

  if (!displayRaw || !initialValues) {
    return (
      <SlideoutDrawerV2
        isOpen={isOpen}
        onClose={onClose}
        isDismissable
        overlayStyle={OverlayStyle.Transparent}
        zIndex={Z_INDEX.drawer}
        data-testid="footnote-settings-panel"
      />
    );
  }

  return (
    <SlideoutDrawerV2
      isOpen={isOpen}
      onClose={onClose}
      onCloseAnimationComplete={handleCloseAnimationComplete}
      isDismissable={false}
      overlayStyle={OverlayStyle.Transparent}
      zIndex={Z_INDEX.drawer}
      data-testid="footnote-settings-panel"
    >
      <div className={styles.drawerWidth} data-testid="footnote-settings-form">
        {/* Keyed on the footnote so opening a different one re-mounts the form.
            `enableReinitialize` resets Formik in place, which the uncontrolled
            footnote-text editor can't follow — it would keep showing (and save)
            the previously selected footnote's body. */}
        <Form<FootnoteSettingsValues>
          key={displayRaw.id}
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={(values) => onSave(values)}
          submitOnlyChangedValues={false}
          onSubmitErrorMessage="Something went wrong saving this footnote"
          enableReinitialize
        >
          <Flex flexDirection="column" className={styles.form}>
            <SlideoutDrawerV2.ComplexHeader
              className={styles.header}
              title="Footnote settings"
              closeButton={
                <Button
                  variant="custom"
                  textColor={Colors.Neutral700}
                  backgroundColor={Colors.Neutral0}
                  icon="xmark"
                  iconPosition="right"
                  data-testid="footnote-settings-close-btn"
                >
                  {isReadOnly ? "Close" : "Cancel"}
                </Button>
              }
              showGap={false}
              showBorder={false}
            />
            <Flex flexDirection="column" gap={SPACING.LARGE} className={styles.body}>
              <Card isFullWidth title="Details">
                <div className={styles.detailsCard}>
                  <FormRadioGroup
                    name="footnoteKind"
                    label="Type"
                    data-testid="footnote-settings-type"
                  >
                    <FormRadioButton value="FREEFORM_TEXT" disabled={isReadOnly}>
                      Freeform text
                    </FormRadioButton>
                    <FormRadioButton
                      value="CUSTOM_FIELD"
                      disabled={isReadOnly || isSystemLevel}
                    >
                      Custom field
                    </FormRadioButton>
                  </FormRadioGroup>
                  <NameField isReadOnly={isReadOnly} />
                  <FootnoteTextField isReadOnly={isReadOnly} mergeTags={mergeTags} />
                  <CustomFieldPicker
                    customFieldOptionGroups={customFieldOptionGroups}
                    isLoadingCustomFields={isLoadingCustomFields}
                    hasCustomFieldsError={hasCustomFieldsError}
                    isReadOnly={isReadOnly}
                  />
                  <FootnoteSymbolField
                    queryFootnoteSymbol={queryFootnoteSymbol}
                    isReadOnly={isReadOnly}
                  />
                  <SymbolMergeField persistedToken={displayRaw.symbolMergeField} />
                </div>
              </Card>
              <RulesSection
                title="Suppression rules"
                description="When do you want to hide this footnote"
                name="suppressionRules"
                isReadOnly={isReadOnly}
                arenaId={arenaId}
                attributeLeaves={attributeLeaves}
              />
            </Flex>
            {!isReadOnly && (
              <Flex alignItems="center" justifyContent="flex-end" className={styles.footer}>
                <SubmitButton data-testid="save-footnote-btn">Save footnote</SubmitButton>
              </Flex>
            )}
          </Flex>
        </Form>
      </div>
    </SlideoutDrawerV2>
  );
};
